import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  memberAreasService,
  Banner,
  CustomMenuItem,
  PlatformSettings,
} from '@/services/member-areas.service';
import { toast } from 'sonner';
import i18n from '@/i18n';

// ==================== BANNERS ====================

export function useBanners() {
  return useQuery({
    queryKey: ['banners', 'admin'],
    queryFn: () => memberAreasService.getBanners(),
  });
}

export function usePublicBanners(position?: string) {
  return useQuery({
    queryKey: ['banners', 'public', position],
    queryFn: () => memberAreasService.getPublicBanners(position),
  });
}

export function useCreateBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Banner> & { order: number }) =>
      memberAreasService.createBanner(
        data as Omit<Banner, 'id' | 'createdAt' | 'updatedAt'>,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      toast.success(i18n.t('adminCms:toasts.bannerCreated'));
    },
    onError: () => {
      toast.error(i18n.t('adminCms:toasts.bannerCreateError'));
    },
  });
}

export function useUpdateBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Banner> }) =>
      memberAreasService.updateBanner(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      toast.success(i18n.t('adminCms:toasts.bannerUpdated'));
    },
    onError: () => {
      toast.error(i18n.t('adminCms:toasts.bannerUpdateError'));
    },
  });
}

export function useDeleteBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => memberAreasService.deleteBanner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      toast.success(i18n.t('adminCms:toasts.bannerDeleted'));
    },
    onError: () => {
      toast.error(i18n.t('adminCms:toasts.bannerDeleteError'));
    },
  });
}

export function useReorderBanners() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => memberAreasService.reorderBanners(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
    },
  });
}

// ==================== MENU ITEMS ====================

export function useMenuItems() {
  return useQuery({
    queryKey: ['menuItems', 'admin'],
    queryFn: () => memberAreasService.getMenuItems(),
  });
}

export function usePublicMenuItems() {
  return useQuery({
    queryKey: ['menuItems', 'public'],
    queryFn: () => memberAreasService.getPublicMenuItems(),
  });
}

export function useCreateMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CustomMenuItem> & { order: number }) =>
      memberAreasService.createMenuItem(
        data as Omit<CustomMenuItem, 'id' | 'createdAt' | 'updatedAt'>,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
      toast.success(i18n.t('adminCms:toasts.menuItemCreated'));
    },
    onError: () => {
      toast.error(i18n.t('adminCms:toasts.menuItemCreateError'));
    },
  });
}

export function useUpdateMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CustomMenuItem> }) =>
      memberAreasService.updateMenuItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
      toast.success(i18n.t('adminCms:toasts.menuItemUpdated'));
    },
    onError: () => {
      toast.error(i18n.t('adminCms:toasts.menuItemUpdateError'));
    },
  });
}

export function useDeleteMenuItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => memberAreasService.deleteMenuItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
      toast.success(i18n.t('adminCms:toasts.menuItemDeleted'));
    },
    onError: () => {
      toast.error(i18n.t('adminCms:toasts.menuItemDeleteError'));
    },
  });
}

export function useReorderMenuItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => memberAreasService.reorderMenuItems(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
    },
  });
}

// ==================== PLATFORM SETTINGS ====================

export function usePlatformSettings() {
  return useQuery({
    queryKey: ['platformSettings'],
    queryFn: () => memberAreasService.getPlatformSettings(),
  });
}

export function useUpdatePlatformSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<PlatformSettings>) =>
      memberAreasService.updatePlatformSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platformSettings'] });
      toast.success(i18n.t('adminCms:toasts.settingsUpdated'));
    },
    onError: () => {
      toast.error(i18n.t('adminCms:toasts.settingsUpdateError'));
    },
  });
}
